import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
/** The API answers an empty body when no monthly budget exists; axios surfaces it as "". */
export function parseMonthlyBudgetResponse(data) {
    return data && typeof data === 'object' ? data : null;
}
function toFiniteNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
export function computeSpendingLimit(monthlyBudget, categoryBudgets) {
    if (monthlyBudget?.amount != null)
        return toFiniteNumber(monthlyBudget.amount);
    return categoryBudgets.reduce((acc, budget) => acc + toFiniteNumber(budget.amount), 0);
}
function userIdParam(scope) {
    if (scope === 'all')
        return {};
    return { userId: scope };
}
export const useCategoryBudgets = (scope, period) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['budgets', 'category', activeOrganizationId, scope, period],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/budgets/category', {
                params: { ...userIdParam(scope), period },
            });
            return data;
        },
    });
};
export const useMonthlyBudget = (scope, period) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['budgets', 'monthly', activeOrganizationId, scope, period],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/budgets/monthly', {
                params: { ...userIdParam(scope), period },
            });
            return parseMonthlyBudgetResponse(data);
        },
    });
};
export const useSetCategoryBudget = (_scope, _period) => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.put('/budgets/category', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['budgets', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
export const useSetMonthlyBudget = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.put('/budgets/monthly', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['budgets', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
export const usePropagateEstimatedIncome = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.put('/budgets/monthly/propagate-estimated-income', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['budgets', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
export const useDeleteCategoryBudget = (scope, period) => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (categoryId) => {
            await apiClient.delete('/budgets/category', {
                params: { categoryId, ...userIdParam(scope), period },
            });
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['budgets', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
